import { ComponentProps } from 'react';

import { LoadingButton, Tooltip } from '@ingrid/luterkort-react';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { useTranslation } from '~src/i18n/use-translation';
import { extractFileName } from '~src/utils/file';

import { useShipmentActions } from '../hooks';
import { useGenerateLabelsUrlMutation } from '../mutations';
import { ShipmentDetails } from '../types';

interface Props {
  shipment: Pick<ShipmentDetails, 'direction_type' | 'status'>;
  size?: ComponentProps<typeof LoadingButton>['size'];
  labelUrl: string | undefined;
}

export function DownloadQrCodeButton(props: Props) {
  const { labelUrl, shipment, size } = props;
  const { t } = useTranslation();

  const actions = useShipmentActions({
    shipment: { direction_type: shipment.direction_type, status: shipment.status.type },
  });

  const mutation = useGenerateLabelsUrlMutation();
  const isDisabled = !actions.includes('PRINT_LABEL');

  async function handleDownload() {
    const { data } = await mutation.mutateAsync({ file_name: extractFileName(labelUrl!) });
    window.open(data.url);
  }

  // Don't show the button if there's no label
  if (!labelUrl) {
    return null;
  }

  return (
    <ConditionalWrapper
      when={isDisabled}
      wrapper={c => (
        <Tooltip content={t('shipment.details.notPrintable')}>
          <span>{c}</span>
        </Tooltip>
      )}
    >
      <LoadingButton
        loading={mutation.isPending}
        onClick={handleDownload}
        disabled={isDisabled}
        variant="secondary"
        size={size}
      >
        {t('common.downloadQrCode')}
      </LoadingButton>
    </ConditionalWrapper>
  );
}
